/**
 * AI Coach Drawer
 * Collapsible 300px right rail on the OKR detail page. Hosts the four AI Coach blocks.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, ChevronLeft, RefreshCw, AlertTriangle, CheckCircle2, Info, AlertCircle, Building2, UserCheck, Lightbulb, ListChecks, Power } from 'lucide-react';
import { useCanUseAI } from '../../hooks/useAIConfig';
import { useAICoach, useRefreshAICoach } from '../../hooks/useAICoach';
import { useAICoachDrawerOpen } from '../../hooks/useAICoachDrawerOpen';
import type { CoachInsightView } from '../../services/aiCoach.service';
import { InsightAccordion, AccordionItem } from './InsightAccordion';
import { AskOKRChat } from './AskOKRChat';
import { PURPLE, PURPLE_BG, PURPLE_GLOW_RGBA } from './ai-theme';

const HEADER_HEIGHT = 72; // matches Header.tsx h-[72px]

interface Props {
  okrId: string;
}

export const AICoachDrawer: React.FC<Props> = ({ okrId }) => {
  const { isAdmin, isEligible, aiEnabled, aiHasKey, isLoading: cfgLoading } = useCanUseAI();

  const [open, setOpen] = useAICoachDrawerOpen();

  const coachQuery = useAICoach(okrId, { enabled: isEligible && aiEnabled });
  const refresh = useRefreshAICoach(okrId);

  // Detect AI_NOT_CONFIGURED from the coach error for the admin empty state.
  const coachErr = coachQuery.error as unknown as { response?: { data?: { code?: string } } } | undefined;
  const isNotConfigured =
    coachErr?.response?.data?.code === 'AI_NOT_CONFIGURED' ||
    (isAdmin && !aiHasKey && !cfgLoading);
  const isConfiguredButDisabled = isAdmin && aiHasKey && !aiEnabled && !cfgLoading;

  // Viewers + unauthenticated never see the drawer.
  if (!isEligible) return null;
  // Non-admins only see the drawer when the tenant has AI configured AND enabled.
  // Admins always see it so they can act on the "configure / enable" empty states.
  if (!isAdmin && !aiEnabled) return null;

  const toggle = (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className="fixed z-30 flex items-center gap-1 px-2 py-3 rounded-l-lg shadow-md text-white text-xs font-medium"
      style={{
        top: `calc(${HEADER_HEIGHT}px + (100vh - ${HEADER_HEIGHT}px) / 2)`,
        transform: 'translateY(-50%)',
        right: open ? 300 : 0,
        background: PURPLE,
        transition: 'right 300ms ease',
      }}
      aria-label={open ? 'Hide AI Coach' : 'Show AI Coach'}
    >
      {open ? <ChevronLeft className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
      {!open && <span>AI Coach</span>}
    </button>
  );

  return (
    <>
      {toggle}
      <aside
        className="fixed right-0 bg-white border-l border-stone-200 shadow-lg z-20 flex flex-col"
        style={{
          top: HEADER_HEIGHT,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          width: 300,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms ease',
        }}
      >
        <header className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 sticky top-0 bg-white">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: PURPLE, boxShadow: `0 0 0 3px ${PURPLE_GLOW_RGBA}` }}
          />
          <span className="font-semibold text-sm flex-1">AI Coach</span>
          {coachQuery.data?.isRefreshing && (
            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
              Refreshing…
            </span>
          )}
          {coachQuery.data && !coachQuery.data.isRefreshing && coachQuery.data.status === 'ready' && (
            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
              Live
            </span>
          )}
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="text-stone-400 hover:text-stone-700"
            aria-label="Refresh AI Coach"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refresh.isPending ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-stone-400 hover:text-stone-700"
            aria-label="Close AI Coach"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Drawer body — flex column: accordion on top, chat fills the rest */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="p-3 space-y-2">
            {isNotConfigured && isAdmin && <NotConfiguredCard />}
            {!isNotConfigured && isConfiguredButDisabled && <ConfiguredButDisabledCard />}

            {!isNotConfigured && !isConfiguredButDisabled && coachQuery.isLoading && (
              <SkeletonBlocks />
            )}
            {!isNotConfigured && !isConfiguredButDisabled && coachQuery.error && !coachQuery.isLoading && (
              <ErrorCard message={(coachQuery.error as Error).message} />
            )}
            {!isNotConfigured && !isConfiguredButDisabled && coachQuery.data && coachQuery.data.status !== 'ready' && (
              <SkeletonBlocks />
            )}
            {!isNotConfigured && !isConfiguredButDisabled && coachQuery.data && coachQuery.data.status === 'ready' && (
              <>
                <InsightAccordion defaultOpenId="quality">
                  {({ isOpen, toggle }) => (
                    <>
                      <AccordionItem
                        id="quality"
                        title="OKR Quality"
                        icon={<Lightbulb className="w-3.5 h-3.5" />}
                        badge={<span className="text-[10px] font-semibold text-stone-500">{coachQuery.data!.qualityBlock.score}/100</span>}
                        isOpen={isOpen('quality')}
                        onToggle={toggle}
                      >
                        <OKRQualityBlock block={coachQuery.data!.qualityBlock} />
                      </AccordionItem>

                      <AccordionItem
                        id="checkin"
                        title="Last check-in quality"
                        icon={<ListChecks className="w-3.5 h-3.5" />}
                        isOpen={isOpen('checkin')}
                        onToggle={toggle}
                      >
                        <LastCheckInQualityBlock block={coachQuery.data!.actionBlock} />
                      </AccordionItem>

                      <AccordionItem
                        id="performance"
                        title="Individual Performance"
                        icon={<UserCheck className="w-3.5 h-3.5" />}
                        isOpen={isOpen('performance')}
                        onToggle={toggle}
                      >
                        <IndividualPerformanceBlock block={coachQuery.data!.performanceBlock} />
                      </AccordionItem>

                      <AccordionItem
                        id="org"
                        title="Org Context"
                        icon={<Building2 className="w-3.5 h-3.5" />}
                        isOpen={isOpen('org')}
                        onToggle={toggle}
                      >
                        <OrgContextBlock block={coachQuery.data!.orgContextBlock} />
                      </AccordionItem>
                    </>
                  )}
                </InsightAccordion>
                <FreshnessFooter coach={coachQuery.data} />
              </>
            )}
          </div>

          {/* Chat lives below the insights, fills remaining height. Hidden when AI not configured/disabled. */}
          {!isNotConfigured && !isConfiguredButDisabled && (
            <AskOKRChat okrId={okrId} />
          )}
        </div>
      </aside>
    </>
  );
};

// ─── Blocks ──────────────────────────────────────────────────

const criterionStyle = (status: 'pass' | 'warn' | 'fail') =>
  status === 'pass'
    ? 'bg-emerald-50 text-emerald-700'
    : status === 'warn'
      ? 'bg-amber-50 text-amber-700'
      : 'bg-red-50 text-red-700';

const OKRQualityBlock: React.FC<{ block: CoachInsightView['qualityBlock'] }> = ({ block }) => {
  const tone =
    block.score >= 80 ? 'text-emerald-600'
      : block.score >= 60 ? 'text-amber-600'
        : 'text-red-600';
  return (
    <>
      <div className="flex items-baseline gap-1 mb-2">
        <span className={`text-3xl font-bold ${tone}`}>{block.score}</span>
        <span className="text-sm text-stone-400">/ 100</span>
        {block.label && (
          <span className={`ml-2 text-[12px] font-semibold ${tone}`}>{block.label}</span>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        {block.criteria.map((c, i) => (
          <span
            key={`${c.name}-${i}`}
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${criterionStyle(c.status)}`}
          >
            {c.status === 'pass' ? <CheckCircle2 className="w-2.5 h-2.5" /> : c.status === 'warn' ? <AlertTriangle className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />}
            {c.name}
          </span>
        ))}
      </div>
      {block.note && <p className="text-[13px] text-stone-600 leading-snug">{block.note}</p>}
    </>
  );
};

const findingIcon = (type: 'good' | 'warn' | 'info' | 'bad') => {
  if (type === 'good') return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />;
  if (type === 'warn') return <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />;
  if (type === 'bad') return <AlertCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />;
  return <Info className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />;
};

const LastCheckInQualityBlock: React.FC<{ block: CoachInsightView['actionBlock'] }> = ({ block }) => (
  <>
    {block.findings.length === 0 ? (
      <p className="text-[13px] text-stone-500">No check-ins yet.</p>
    ) : (
      <ul className="space-y-1.5">
        {block.findings.map((f, i) => (
          <li key={i} className="flex gap-2 items-start">
            {findingIcon(f.type)}
            <p className="text-[13px] text-stone-700 leading-snug">{f.text}</p>
          </li>
        ))}
      </ul>
    )}
  </>
);

const IndividualPerformanceBlock: React.FC<{ block: CoachInsightView['performanceBlock'] }> = ({ block }) => {
  const scoreTone =
    block.score == null ? 'text-stone-400' : block.score >= 70 ? 'text-emerald-600' : block.score >= 40 ? 'text-amber-600' : 'text-red-600';
  const trendTone =
    block.trend == null ? 'text-stone-400' : block.trend >= 0 ? 'text-emerald-600' : 'text-red-600';
  const trendText =
    block.trend == null ? '—' : `${block.trend > 0 ? '+' : ''}${block.trend} pts`;
  return (
    <>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div className="bg-white border border-stone-200 rounded-lg p-2 text-center">
          <div className={`text-xl font-bold ${scoreTone}`}>
            {block.score == null ? '—' : block.score}
          </div>
          <div className="text-[10px] text-stone-500 mt-0.5">Latest score</div>
        </div>
        <div className="bg-white border border-stone-200 rounded-lg p-2 text-center">
          <div className={`text-xl font-bold ${trendTone}`}>{trendText}</div>
          <div className="text-[10px] text-stone-500 mt-0.5">Trend</div>
        </div>
      </div>
      {block.note && <p className="text-[13px] text-stone-600 leading-snug">{block.note}</p>}
    </>
  );
};

const OrgContextBlock: React.FC<{ block: CoachInsightView['orgContextBlock'] }> = ({ block }) => (
  <>
    <p className="text-[13px] text-stone-600 leading-relaxed whitespace-pre-wrap">
      {block.text || '—'}
    </p>
  </>
);

const FreshnessFooter: React.FC<{ coach: CoachInsightView }> = ({ coach }) => {
  if (!coach.generatedAt) return null;
  return (
    <div className="text-[10px] text-stone-400 text-center pt-1 pb-2">
      Generated {new Date(coach.generatedAt).toLocaleString()}
      {coach.tokensUsed != null && ` · ${coach.tokensUsed.toLocaleString()} tokens`}
    </div>
  );
};

const SkeletonBlocks: React.FC = () => (
  <>
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="bg-stone-50 border border-stone-200 rounded-xl p-3 animate-pulse">
        <div className="h-3 bg-stone-200 rounded w-1/3 mb-3" />
        <div className="h-2 bg-stone-200 rounded w-full mb-2" />
        <div className="h-2 bg-stone-200 rounded w-5/6" />
      </div>
    ))}
  </>
);

const ErrorCard: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-red-50 border border-red-200 rounded-xl p-3">
    <div className="flex items-start gap-2">
      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
      <div className="text-[13px] text-red-700">
        <div className="font-semibold mb-0.5">Couldn't generate insights</div>
        <div>{message}</div>
      </div>
    </div>
  </div>
);

const NotConfiguredCard: React.FC = () => (
  <div className="border border-dashed rounded-xl p-4" style={{ borderColor: PURPLE, background: PURPLE_BG }}>
    <div className="flex items-start gap-3">
      <Sparkles className="w-5 h-5 shrink-0" style={{ color: PURPLE }} />
      <div className="text-[13px]" style={{ color: '#4a2080' }}>
        <div className="font-semibold mb-1">AI is not configured yet</div>
        <p className="leading-snug mb-2">
          Add your Anthropic or OpenAI key in Settings to unlock the AI Coach, inline hints,
          and check-in assessments for your organization.
        </p>
        <Link
          to="/admin?tab=settings"
          className="inline-block text-[13px] font-medium underline"
          style={{ color: PURPLE }}
        >
          Configure AI →
        </Link>
      </div>
    </div>
  </div>
);

const ConfiguredButDisabledCard: React.FC = () => (
  <div className="border border-dashed rounded-xl p-4" style={{ borderColor: PURPLE, background: PURPLE_BG }}>
    <div className="flex items-start gap-3">
      <Power className="w-5 h-5 shrink-0" style={{ color: PURPLE }} />
      <div className="text-[13px]" style={{ color: '#4a2080' }}>
        <div className="font-semibold mb-1">AI is configured but turned off</div>
        <p className="leading-snug mb-2">
          Your API key is saved. Flip the "Enable AI features" switch in Settings to
          start using the AI Coach and inline hints.
        </p>
        <Link
          to="/admin?tab=settings"
          className="inline-block text-[13px] font-medium underline"
          style={{ color: PURPLE }}
        >
          Enable AI →
        </Link>
      </div>
    </div>
  </div>
);
